import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { registerTokenAccessor, APIError } from "@/lib/api/client";
import { authTokenStorage } from "@/lib/storage/authTokenStorage";
import { pendingReviewStorage } from "@/lib/storage/pendingReviewStorage";
import { tempPasswordStore } from "@/lib/tempPasswordStore";
import { authApi } from "@/lib/api/auth";
import type { AuthUser as ApiAuthUser, UserRole, VendorStatus } from "@chinese-vendor/schemas";

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: UserRole[];
  vendorStatus: VendorStatus | null;
  applicationId: string | null;
  temporaryPasswordSet: boolean;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isPendingApplication: boolean;
  hasDismissedPendingReview: boolean;
}

interface AuthContextValue extends AuthState {
  login: (token: string, user: AuthUser) => Promise<void>;
  logout: () => Promise<void>;
  setVendorStatus: (status: VendorStatus) => void;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  isPendingApplication: boolean;
  dismissPendingReview: () => void;
  clearTemporaryPassword: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toAuthUser(apiUser: ApiAuthUser): AuthUser {
  return {
    id: apiUser.id,
    email: apiUser.email,
    firstName: apiUser.first_name,
    lastName: apiUser.last_name,
    roles: apiUser.roles,
    vendorStatus: apiUser.vendor_status,
    applicationId: apiUser.application_id ?? null,
    temporaryPasswordSet: apiUser.temporary_password_set ?? false,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    token: null,
    user: null,
    isAuthenticated: false,
    isLoading: true,
    isPendingApplication: false,
    hasDismissedPendingReview: false,
  });

  // Stable ref so the token accessor always returns the latest value.
  const tokenRef = useRef<string | null>(null);

  // Re-register on every token change so auth flows that temporarily
  // overwrite the accessor (authApi.loginWithPassword etc.) don't leave
  // the client pointing at a stale closure after logout.
  useEffect(() => {
    registerTokenAccessor(() => tokenRef.current);
  }, [state.token]);

  // Restore session from storage on app launch, then fetch the user profile
  // so that deep links and page reloads work without requiring a re-login.
  useEffect(() => {
    Promise.all([authTokenStorage.get(), pendingReviewStorage.get()])
      .then(async ([stored, dismissed]) => {
        if (stored) {
          tokenRef.current = stored;
          try {
            const user = toAuthUser(await authApi.me());
            setState({
              token: stored,
              user,
              isAuthenticated: true,
              isLoading: false,
              isPendingApplication: user.vendorStatus === "pending_application",
              hasDismissedPendingReview: dismissed,
            });
          } catch (err) {
            // Only clear the session for auth errors (token rejected by server).
            // Transient network errors should not log the user out.
            if (err instanceof APIError && (err.status === 401 || err.status === 403)) {
              tokenRef.current = null;
              await authTokenStorage.remove();
              setState((prev) => ({ ...prev, token: null, isLoading: false }));
            } else {
              // Network/server error — keep the token, unblock the app.
              setState((prev) => ({ ...prev, isLoading: false }));
            }
          }
        } else {
          setState((prev) => ({ ...prev, isLoading: false, hasDismissedPendingReview: dismissed }));
        }
      })
      .catch(() => {
        setState((prev) => ({ ...prev, isLoading: false }));
      });
  }, []);

  const login = useCallback(async (token: string, user: AuthUser) => {
    tokenRef.current = token;
    try {
      await authTokenStorage.set(token);
    } catch (error) {
      console.warn("Failed to persist auth token", error);
    }
    setState((prev) => ({
      ...prev,
      token,
      user,
      isAuthenticated: true,
      isLoading: false,
      isPendingApplication: user.vendorStatus === "pending_application",
    }));
  }, []);

  const logout = useCallback(async () => {
    // Call the backend first while the token is still attached to requests,
    // so the server can blacklist it. Best-effort: a network failure must not
    // prevent the local session from being cleared.
    try {
      await authApi.logout();
    } catch {
      // Silently ignore — local logout proceeds regardless.
    }
    tokenRef.current = null;
    try {
      await authTokenStorage.remove();
    } catch (error) {
      console.warn("Failed to remove auth token", error);
    }
    pendingReviewStorage.remove();
    tempPasswordStore.clear();
    setState({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isPendingApplication: false,
      hasDismissedPendingReview: false,
    });
  }, []);

  const setVendorStatus = useCallback((status: VendorStatus) => {
    setState((prev) => ({
      ...prev,
      user: prev.user ? { ...prev.user, vendorStatus: status } : prev.user,
    }));
  }, []);

  const hasRole = useCallback(
    (role: UserRole): boolean => state.user?.roles.includes(role) ?? false,
    [state.user]
  );

  const hasAnyRole = useCallback(
    (roles: UserRole[]): boolean => roles.some((role) => state.user?.roles.includes(role) ?? false),
    [state.user]
  );

  const dismissPendingReview = useCallback(() => {
    pendingReviewStorage.set();
    setState((prev) => ({ ...prev, hasDismissedPendingReview: true }));
  }, []);

  const clearTemporaryPassword = useCallback(() => {
    setState((prev) => ({
      ...prev,
      user: prev.user ? { ...prev.user, temporaryPasswordSet: false } : prev.user,
    }));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        setVendorStatus,
        hasRole,
        hasAnyRole,
        dismissPendingReview,
        clearTemporaryPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
